import { PARALLEL_JOB_ACTIONS, isNullable } from '@ipfabric/utils';
import { createSelector } from 'reselect';
import { JOB_ACTIONS, JOB_STATUSES } from './constants';

const selectDomain = () => (state) => state.get('jobManagement');

const makeSelectDomain = () =>
  createSelector(selectDomain(), (substate) => substate);

const makeSelectCancelling = () =>
  createSelector(makeSelectDomain(), (substate) => substate.get('cancelling'));
const makeSelectReading = () =>
  createSelector(makeSelectDomain(), (substate) => substate.get('reading'));
const makeSelectStopping = () =>
  createSelector(makeSelectDomain(), (substate) => substate.get('stopping'));
const makeSelectJobs = () =>
  createSelector(makeSelectDomain(), (substate) => substate.get('jobs'));
const makeSelectStopJobId = () =>
  createSelector(makeSelectDomain(), (substate) => substate.get('stopJobId'));
const makeSelectStopJobAction = () =>
  createSelector(makeSelectDomain(), (substate) =>
    substate.get('stopJobAction'),
  );
/**
 * Is `job` still occupying `snapshotId`, i.e. does it block snapshot actions
 * (load/unload, clone, delete, lock, download)?
 *
 * `done` is essential: the job list holds every *unread* job, and jobs the
 * backend keeps unread on purpose (`alwaysNotify` - aimFlowPathLookup,
 * aimImport) stay in it after finishing. Without this check they would keep
 * their snapshot "busy" until the user cleared the notification or reloaded the
 * page (the `job:queued` seed drops done jobs that have no download file).
 */
const isUnfinishedSnapshotJob = (job, snapshotId) =>
  (job.get('snapshot') === snapshotId ||
    job.getIn(['args', 'vCollectKey']) === snapshotId) &&
  !job.get('done') &&
  job.get('status') !== JOB_STATUSES.error &&
  !PARALLEL_JOB_ACTIONS.includes(job.get('action')) &&
  job.get('action') !== JOB_ACTIONS.techsupport;

const makeSelectSnapshotIsInJobs = () =>
  createSelector(
    selectDomain(),
    (_state, props) => props.snapshotId,
    (substate, snapshotId) =>
      substate
        .get('jobs')
        .some((job) => isUnfinishedSnapshotJob(job, snapshotId)),
  );
const makeSelectDiscoveryIsInJobs = () =>
  createSelector(
    selectDomain(),
    (_state, props) => props.snapshotId,
    (substate, snapshotId) => {
      const jobs = substate.get('jobs');
      if (isNullable(snapshotId)) {
        return false;
      }
      return jobs.some(
        (job) =>
          (job.get('snapshot') === snapshotId ||
            job.getIn(['args', 'vCollectKey']) === snapshotId) &&
          job.get('status') !== JOB_STATUSES.error &&
          job.get('action') === JOB_ACTIONS.discovery &&
          job.get('action') !== JOB_ACTIONS.snapshotDownload &&
          job.get('action') !== JOB_ACTIONS.techsupport,
      );
    },
  );
const makeSelectSnapshotInJobs = () =>
  createSelector(
    selectDomain(),
    (_state, props) => props.snapshotId,
    // Consumers read `startedAt` off it to render in-progress state, so a
    // finished job must not be returned either.
    (substate, snapshotId) =>
      substate
        .get('jobs')
        .find((job) => isUnfinishedSnapshotJob(job, snapshotId)),
  );
const makeSelectDiscoveryStarting = () =>
  createSelector(selectDomain(), (substate) => {
    const jobs = substate.get('jobs');
    return jobs.some(
      (job) =>
        job.get('snapshot') === null &&
        job.get('status') !== JOB_STATUSES.scheduled &&
        job.get('status') !== JOB_STATUSES.error &&
        !PARALLEL_JOB_ACTIONS.includes(job.get('action')) &&
        job.get('action') !== JOB_ACTIONS.techsupport,
    );
  });
const makeSelectDiscoveryRefreshScheduled = () =>
  createSelector(
    selectDomain(),
    (_state, props) => props.snapshotId,
    (substate, snapshotId) => {
      const jobs = substate.get('jobs');
      return jobs.some(
        (job) =>
          job.getIn(['args', 'vCollectKey']) === snapshotId &&
          job.get('status') === JOB_STATUSES.scheduled &&
          job.getIn(['args', 'action']) === 'refresh',
      );
    },
  );
const makeSelectDiscoveryAddScheduled = () =>
  createSelector(
    selectDomain(),
    (_state, props) => props.snapshotId,
    (substate, snapshotId) => {
      const jobs = substate.get('jobs');
      return jobs.some(
        (job) =>
          job.getIn(['args', 'vCollectKey']) === snapshotId &&
          job.get('status') === JOB_STATUSES.scheduled &&
          job.getIn(['args', 'action']) === 'add',
      );
    },
  );
const makeSelectDeletingDevices = () =>
  createSelector(
    selectDomain(),
    (_state, props) => props.snapshotId,
    (substate, snapshotId) => {
      const jobs = substate.get('jobs');
      if (isNullable(snapshotId)) {
        return false;
      }
      return jobs.some(
        (job) =>
          job.get('snapshot') === snapshotId &&
          (job.get('status') === JOB_STATUSES.scheduled ||
            job.get('status') === JOB_STATUSES.running) &&
          job.get('action') === JOB_ACTIONS.deleteDevice,
      );
    },
  );
const makeSelectDiscoveryRunByUser = () =>
  createSelector(selectDomain(), (substate) => {
    const jobs = substate.get('jobs');
    const foundJob = !jobs
      ? false
      : jobs.find(
          (job) =>
            job.get('action') === 'discovery' &&
            job.get('cron') === false &&
            job.get('status') !== 'error',
        );
    if (!foundJob) {
      return false;
    }
    return true;
  });
const makeSelectDiscoveryRunByUserId = (userId) => (state) => {
  const jobs = state.getIn(['jobManagement', 'jobs']);
  const foundJob = !jobs
    ? false
    : jobs.find(
        (job) =>
          job.get('action') === 'discovery' &&
          job.get('cron') === false &&
          job.get('status') !== 'error' &&
          job.get('userId') === userId,
      );
  if (!foundJob) {
    return false;
  }
  return true;
};
const makeSelectScheduledLoading = () =>
  createSelector(
    selectDomain(),
    (_state, props) => props.snapshotId,
    (substate, snapshotId) => {
      const jobs = substate.get('jobs');
      return jobs.some(
        (job) =>
          job.get('status') === JOB_STATUSES.scheduled &&
          job.get('action') === JOB_ACTIONS.snapshotLoad &&
          job.get('snapshot') === snapshotId,
      );
    },
  );
const makeSelectScheduledUnloading = () =>
  createSelector(
    selectDomain(),
    (_state, props) => props.snapshotId,
    (substate, snapshotId) => {
      const jobs = substate.get('jobs');
      return jobs.some(
        (job) =>
          job.get('status') === JOB_STATUSES.scheduled &&
          job.get('action') === JOB_ACTIONS.snapshotUnload &&
          job.get('snapshot') === snapshotId,
      );
    },
  );
const makeSelectScheduledDelete = () =>
  createSelector(
    selectDomain(),
    (_state, props) => props.snapshotId,
    (substate, snapshotId) => {
      const jobs = substate.get('jobs');
      return jobs.some(
        (job) =>
          job.get('status') === JOB_STATUSES.scheduled &&
          job.get('action') === JOB_ACTIONS.snapshotDelete &&
          job.get('snapshot') === snapshotId,
      );
    },
  );
const makeSelectScheduledClone = () =>
  createSelector(
    selectDomain(),
    (_state, props) => props.snapshotId,
    (substate, snapshotId) => {
      const jobs = substate.get('jobs');
      return jobs.some(
        (job) =>
          job.get('status') === JOB_STATUSES.scheduled &&
          job.get('action') === JOB_ACTIONS.snapshotClone &&
          job.get('snapshot') === snapshotId,
      );
    },
  );
const makeSelectScheduledSnapshotDownload = () =>
  createSelector(
    selectDomain(),
    (_state, props) => props.snapshotId,
    (substate, snapshotId) => {
      const jobs = substate.get('jobs');
      return jobs.some(
        (job) =>
          job.get('status') === JOB_STATUSES.scheduled &&
          job.get('action') === JOB_ACTIONS.snapshotDownload &&
          job.get('snapshot') === snapshotId,
      );
    },
  );
const makeSelectSnapshotDownloadRunning = () =>
  createSelector(
    selectDomain(),
    (_state, props) => props.snapshotId,
    (substate, snapshotId) => {
      const jobs = substate.get('jobs');
      return jobs.some(
        (job) =>
          job.get('status') === JOB_STATUSES.running &&
          job.get('action') === JOB_ACTIONS.snapshotDownload &&
          job.get('snapshot') === snapshotId,
      );
    },
  );
const makeSelectTechsupportGenerating = () =>
  createSelector(selectDomain(), (substate) => {
    const jobs = substate.get('jobs');
    return jobs.find(
      (job) =>
        [
          JOB_STATUSES.scheduled,
          JOB_STATUSES.running,
          JOB_STATUSES.stopping,
        ].includes(job.get('status')) &&
        job.get('action') === JOB_ACTIONS.techsupport,
    );
  });
const makeSelectSnapshotVerifyRunning = (snapshotId) =>
  createSelector(selectDomain(), (substate) => {
    const jobs = substate.get('jobs');
    return jobs.find(
      (job) =>
        [
          JOB_STATUSES.scheduled,
          JOB_STATUSES.running,
          JOB_STATUSES.stopping,
        ].includes(job.get('status')) &&
        job.get('action') === JOB_ACTIONS.snapshotVerify &&
        job.getIn(['args', 'snapshot', 'id']) === snapshotId,
    );
  });
const makeSelectAnySnapshotVerifyRunning = () =>
  createSelector(selectDomain(), (substate) => {
    const jobs = substate.get('jobs');
    return jobs.find(
      (job) =>
        [
          JOB_STATUSES.scheduled,
          JOB_STATUSES.running,
          JOB_STATUSES.stopping,
        ].includes(job.get('status')) &&
        job.get('action') === JOB_ACTIONS.snapshotVerify,
    );
  });

export {
  makeSelectAnySnapshotVerifyRunning,
  makeSelectCancelling,
  makeSelectDeletingDevices,
  makeSelectDiscoveryAddScheduled,
  makeSelectDiscoveryIsInJobs,
  makeSelectDiscoveryRefreshScheduled,
  makeSelectDiscoveryRunByUser,
  makeSelectDiscoveryRunByUserId,
  makeSelectDiscoveryStarting,
  makeSelectJobs,
  makeSelectReading,
  makeSelectScheduledClone,
  makeSelectScheduledDelete,
  makeSelectScheduledSnapshotDownload,
  makeSelectScheduledLoading,
  makeSelectScheduledUnloading,
  makeSelectSnapshotDownloadRunning,
  makeSelectSnapshotInJobs,
  makeSelectSnapshotIsInJobs,
  makeSelectStopJobAction,
  makeSelectStopJobId,
  makeSelectStopping,
  makeSelectSnapshotVerifyRunning,
  makeSelectTechsupportGenerating,
};
